/**
 * Single source for reading product COGS, price, and margin in list/card/table views.
 * Uses only the product row and its own worldStore entry (no product-group merging).
 */

function parsePositiveMoney (value) {
  if (value === null || value === undefined || value === '') return null
  const n = parseFloat(value)
  if (isNaN(n) || n <= 0) return null
  return n
}

export function getCogsFromRecord (record) {
  if (!record) return null
  return (
    parsePositiveMoney(record.cogs) ||
    parsePositiveMoney(record.supplierCost) ||
    parsePositiveMoney(record.supplier_cost)
  )
}

export function getBuyBoxPriceFromRecord (record) {
  if (!record) return null
  return (
    parsePositiveMoney(record.buyBoxPrice) ||
    parsePositiveMoney(record.buy_box_price) ||
    parsePositiveMoney(record.currentPrice) ||
    parsePositiveMoney(record.current_price)
  )
}

/** List row + catalog/worldStore entry for this productId only. */
function recordsForLookup (product, productsById, worldStoreProducts) {
  if (!product) return []
  const out = [product]
  const id = product.productId
  if (!id) return out
  if (productsById && productsById[id] && productsById[id] !== product) {
    out.push(productsById[id])
  }
  if (worldStoreProducts && worldStoreProducts[id] && worldStoreProducts[id] !== product) {
    out.push(worldStoreProducts[id])
  }
  return out
}

/** Product map from API page data or worldStore (walks up the Vue tree). */
export function findProductsById (vm) {
  let cur = vm
  while (cur) {
    if (cur.apiData && cur.apiData.products) return cur.apiData.products
    if (cur.worldStore && cur.worldStore.products) return cur.worldStore.products
    if (cur.productData && cur.productData.products) return cur.productData.products
    cur = cur.$parent
  }
  return null
}

/** Cached IndexPage worldStore.products (walks up the Vue tree). */
export function findWorldStoreProducts (vm) {
  let cur = vm
  while (cur) {
    if (cur.worldStore && cur.worldStore.products) return cur.worldStore.products
    cur = cur.$parent
  }
  return null
}

export function getProductCatalog (vm) {
  return {
    productsById: findProductsById(vm),
    worldStoreProducts: findWorldStoreProducts(vm)
  }
}

export function resolveProductCogs (product, productsById, worldStoreProducts) {
  const records = recordsForLookup(product, productsById, worldStoreProducts)
  for (let i = 0; i < records.length; i++) {
    const cogs = getCogsFromRecord(records[i])
    if (cogs != null) return cogs
  }
  return null
}

export function resolveProductBuyBoxPrice (product, productsById, worldStoreProducts) {
  const records = recordsForLookup(product, productsById, worldStoreProducts)
  for (let i = 0; i < records.length; i++) {
    const price = getBuyBoxPriceFromRecord(records[i])
    if (price != null) return price
  }
  return null
}

function marginFromRecord (record) {
  if (!record || record.margin === null || record.margin === undefined) return null
  const m = parseFloat(record.margin)
  if (isNaN(m)) return null
  return Math.round(m)
}

export function resolveProductMargin (product, productsById, worldStoreProducts) {
  const records = recordsForLookup(product, productsById, worldStoreProducts)
  for (let i = 0; i < records.length; i++) {
    const m = marginFromRecord(records[i])
    if (m != null) return m
  }
  const cogs = resolveProductCogs(product, productsById, worldStoreProducts)
  const price = resolveProductBuyBoxPrice(product, productsById, worldStoreProducts)
  if (cogs == null || price == null) return null
  return Math.round(((price - cogs) / price) * 100)
}

export function hasProductCogs (product, productsById, worldStoreProducts) {
  return resolveProductCogs(product, productsById, worldStoreProducts) != null
}

/**
 * Bound reader — use one instance per view (card + table share the same catalog rules).
 */
export function createProductReader (catalog) {
  const productsById = catalog && catalog.productsById ? catalog.productsById : null
  const worldStoreProducts = catalog && catalog.worldStoreProducts ? catalog.worldStoreProducts : null

  return {
    hasCogs (product) {
      return hasProductCogs(product, productsById, worldStoreProducts)
    },
    getCogs (product) {
      return resolveProductCogs(product, productsById, worldStoreProducts)
    },
    getMargin (product) {
      return resolveProductMargin(product, productsById, worldStoreProducts)
    },
    getBuyBoxPrice (product) {
      return resolveProductBuyBoxPrice(product, productsById, worldStoreProducts)
    },
    /** Same merged product shape for card and table rows. */
    read (product) {
      if (!product) return null
      const cogs = resolveProductCogs(product, productsById, worldStoreProducts)
      const margin = resolveProductMargin(product, productsById, worldStoreProducts)
      const buyBoxPrice = resolveProductBuyBoxPrice(product, productsById, worldStoreProducts)
      return Object.assign({}, product, {
        cogs: cogs != null ? cogs : product.cogs,
        margin: margin != null ? margin : product.margin,
        buyBoxPrice: buyBoxPrice != null ? buyBoxPrice : product.buyBoxPrice
      })
    }
  }
}

export function createProductReaderFromVm (vm) {
  return createProductReader(getProductCatalog(vm))
}



// WEBPACK FOOTER //
// ./src/utils/productPricing.js